import { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { marked } from "marked";
import api from "@/lib/api";
import { Loader2, ArrowLeft, Calendar, ArrowRight } from "lucide-react";
import { useSEO } from "@/lib/useSEO";

marked.setOptions({ breaks: true, gfm: true });

export default function BlogPost() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/articles/${slug}`);
        setArticle(data);
      } catch {
        setNotFound(true);
      } finally { setLoading(false); }
    })();
  }, [slug]);

  useSEO(article ? {
    title: article.seo_title || article.title,
    description: article.seo_description || article.excerpt || "",
    keywords: (article.tags || []).join(", "),
    image: article.cover_image_url || undefined,
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": article.title,
      "image": article.cover_image_url ? [article.cover_image_url] : undefined,
      "datePublished": article.published_at,
      "dateModified": article.updated_at,
      "author": { "@type": "Person", "name": article.author || "Suraj Dubey" },
      "publisher": {
        "@type": "Organization",
        "name": "ScholarPrintHub",
        "logo": { "@type": "ImageObject", "url": "https://images.pexels.com/photos/14180701/pexels-photo-14180701.jpeg" },
      },
      "description": article.seo_description || article.excerpt || "",
      "keywords": (article.tags || []).join(", "),
    },
  } : { title: undefined });

  if (loading) return <div className="container-app py-24 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-navy" /></div>;
  if (notFound) return <Navigate to="/blog" replace />;
  if (!article) return null;

  const html = marked.parse(article.content || "");

  return (
    <article data-testid={`blog-post-${article.slug}`} className="container-app py-12">
      <Link to="/blog" className="text-xs section-eyebrow hover:underline inline-flex items-center gap-1"><ArrowLeft className="w-3 h-3" /> Back to blog</Link>

      <header className="mt-6 max-w-3xl">
        <div className="flex items-center gap-3 text-[10px] font-mono uppercase tracking-widest text-royal">
          {(article.tags || []).map((t) => (<span key={t}>#{t}</span>))}
        </div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-navy mt-4">{article.title}</h1>
        <div className="divider-gold mt-6 mb-4" />
        <p className="text-slate-600 text-lg">{article.excerpt}</p>
        <div className="mt-4 text-xs text-slate-500 flex items-center gap-4">
          <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {article.published_at ? new Date(article.published_at).toLocaleDateString("en-IN") : ""}</span>
          <span>By {article.author}</span>
        </div>
      </header>

      {article.cover_image_url && (
        <img src={article.cover_image_url} alt={article.title} className="w-full max-h-[420px] object-cover border border-slate-200 rounded-sm mt-10" />
      )}

      <div
        className="prose-blog mt-12 max-w-3xl text-slate-800 leading-relaxed text-[15px]"
        dangerouslySetInnerHTML={{ __html: html }}
      />

      {/* CTA */}
      <div className="max-w-3xl mt-14 border border-slate-200 rounded-sm bg-navy text-white p-8 grid sm:grid-cols-2 gap-6">
        <div>
          <span className="section-eyebrow text-gold">Need help with this?</span>
          <h3 className="font-display text-2xl mt-1">Get a personalized quotation.</h3>
          <p className="text-xs text-slate-300 mt-2">Editing, formatting, plagiarism review, printing & doorstep delivery.</p>
        </div>
        <div className="flex flex-wrap gap-2 items-center justify-end">
          <Link to="/upload" className="btn-gold">Upload PDF</Link>
          <Link to="/academic-request" className="btn-outline border-white text-white hover:bg-white hover:text-navy">Request Help</Link>
        </div>
      </div>

      {(article.related || []).length > 0 && (
        <section className="max-w-3xl mt-14">
          <span className="section-eyebrow">Related reads</span>
          <div className="divider-gold mt-2 mb-6" />
          <div className="grid md:grid-cols-3 gap-4">
            {article.related.map((r) => (
              <Link key={r.id} to={`/blog/${r.slug}`} className="card-flat !p-4 flex flex-col">
                <div className="text-[10px] font-mono uppercase tracking-widest text-royal">{(r.tags || [])[0] || ""}</div>
                <div className="font-display text-lg text-navy mt-2 leading-tight">{r.title}</div>
                <div className="mt-3 text-xs text-royal inline-flex items-center gap-1">Read <ArrowRight className="w-3 h-3" /></div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </article>
  );
}

function ensureMeta(name) {
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  return el;
}
